/* eslint-disable react/prop-types */
import React from 'react';
import { Box, Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useGetReportQuery } from '../../../../../../entities/reports/reportsResource/redux/reportsResource.api';
import Error from '../../../../../../shared/ui/Error/Error';
import useShowError from '../../../../../../shared/lib/hooks/useShowError';
import { postReportGenerate } from '../../../../../../shared/api/api';
import useTimeZone from '../../../../../../entities/timeZone/hooks/useTimeZone';
import { isValidField, toDtoValue } from '../../utils';
import { EMPTY_NAME, fileType } from '../../constants';
import renderControl from '../../utils/renderControl';
import SingleSelect from '../../../../../../shared/ui/SingleSelect';
import Form from './styles/Form';
import Container from './styles/Container';
import Wrapper from './styles/Wrapper';
import ButtonWrapper from './styles/ButtonWrapper';

function DefaultReport({ companyId, controls, setControls, selectedType, setSelectedType }) {
  const [searchParams] = useSearchParams();
  const reportId = parseInt(searchParams.get('report'), 10);

  const report = useGetReportQuery({ id: reportId });

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  const [_, setIsChangeForm] = React.useState(false);
  const [isLoadingFile, setLoadingFile] = React.useState(false);

  const navigate = useNavigate();

  const handleChangeType = (event) => {
    setIsChangeForm(true);
    setSelectedType(event.target.value);
  };

  const handleChangeValueControl = (id, value, helpData) => {
    setControls((prev) => {
      const newControls = [...prev];
      const index = newControls.findIndex((control) => control.id === id);
      newControls[index] = {
        ...newControls[index],
        value,
      };
      if (helpData) newControls[index].helpData = helpData;

      return newControls;
    });
  };

  const handleChangeText = (event) => {
    handleChangeValueControl(event.target.id, event.target.value);
  };

  const handleChangeDate = (date, name) => {
    handleChangeValueControl(name, date);
  };

  const handleChangeAutocomplete = (id, value) => {
    handleChangeValueControl(id, value);
  };

  const handleChangeRange = (id, value, helpData) => {
    handleChangeValueControl(id, value, helpData);
  };

  const isValidForm = () => {
    let isValid = true;
    const newControls = [...controls];
    controls.forEach((item, index) => {
      const isError = !isValidField(item);
      if (isError) {
        isValid = false;
      }
      newControls[index] = {
        ...newControls[index],
        isError,
      };
    });

    setControls(newControls);
    return isValid;
  };

  const showError = useShowError();

  const timeZone = useTimeZone();

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!isValidForm()) {
      return;
    }
    const params = controls.reduce(
      (prev, item) => ({
        ...prev,
        [item.code]: toDtoValue(item, timeZone),
      }),
      {},
    );
    const body = {
      docType: selectedType,
      reportId,
      params,
      reportTemplate: {
        reportTemplateId: report?.data?.reportTemplates?.[0]?.reportTemplateId,
      },
    };
    setLoadingFile(true);
    postReportGenerate(body)
      .then((response) => {
        const octetStreamMime = 'application/octet-stream';
        const cd = response.headers['content-disposition'];
        const regex = /filename[^;=\n]*\*=UTF-8''((['"]).*?\2|[^;\n]*)/;
        const match = regex.exec(cd);
        let fileName = match[1];
        fileName = decodeURIComponent(fileName).replace(/"/g, '');
        const contentType = response.headers['content-type'] || octetStreamMime;

        try {
          const blob = new Blob([response.data], { type: contentType });

          if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, fileName);
          } else {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        } catch (exc) {
          // eslint-disable-next-line no-console
          console.error(exc);
          showError('При сохранении файла отчетов произошла ошибка.');
        }
      })
      .catch(() => showError('Не удалось построить отчет.'))
      .finally(() => {
        setIsChangeForm(false);
        setLoadingFile(false);
      });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Typography variant="h6" sx={{ color: 'text.secondary' }} gutterBottom>
        {report?.data?.name || report?.data?.reportId || EMPTY_NAME}
      </Typography>
      <Container>
        {controls?.map((control) => (
          <Box key={control.id}>
            {renderControl(
              control,
              { companyId, isSchedule: false },
              {
                handleChangeText,
                handleChangeDate,
                handleChangeAutocomplete,
                handleChangeRange,
              },
            )}
          </Box>
        ))}
        {!report?.data?.reportParams?.length && <Error errorMessage="Пустой шаблон" />}
        <Wrapper>
          <SingleSelect
            value={selectedType}
            items={fileType}
            onChange={handleChangeType}
            label="Тип документа"
            name="fileType-label"
          />
        </Wrapper>
        <ButtonWrapper>
          <Button variant="contained" onClick={() => navigate(-1)} disabled={isLoadingFile}>
            Назад
          </Button>
          <Button component={Link} to={`?report=${reportId}&schedule=true`} variant="contained">
            По расписанию
          </Button>
          <Button variant="contained" type="submit" disabled={isLoadingFile}>
            {isLoadingFile ? 'Загрузка...' : 'Построить отчет'}
          </Button>
        </ButtonWrapper>
      </Container>
    </Form>
  );
}

export default DefaultReport;
